import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  DataGridPremium,
  GridEditInputCell,
  GridEditSingleSelectCell,
  gridEditRowsStateSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
  type GridColDef,
  type GridRenderCellParams,
  type GridRenderEditCellParams,
  type GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useAiModelCatalogs } from '@/hooks/useAiAssistantSettings';
import { addRowShortcutLabel } from '@/lib/gridShortcuts';
import { toIncludeSelection } from '@/lib/gridSelection';
import { GRID_ROW_HEIGHT, hoverRevealGridSx, unsavedRowsSx } from '@/lib/gridStyles';
import { useInlineRowEditing } from '@/lib/useInlineRowEditing';
import { aiModelLabel } from '@/pages/core/aiModelLabel';
import { sectionHeadingSx } from '@/pages/core/sectionHeadingSx';
import { moveRow, newRow, type DraftRow, type RoleModelsDraft } from '@/pages/core/useRoleModelsDraft';
import {
  AI_PROVIDERS,
  type AiAssistantSettings,
  type AiModelOption,
  type AiProvider,
} from '@/services/aiAssistantSettingsService';

type Catalogs = Record<AiProvider, AiModelOption[]>;
type ModelEditCellProps = GridRenderEditCellParams<DraftRow> & { catalogs: Catalogs };

const NO_SELECTION: GridRowSelectionModel = { type: 'include', ids: new Set() };

const offeredBy = (catalogs: Catalogs, provider: AiProvider | undefined) =>
  (provider ? catalogs[provider] ?? [] : []).filter((m) => m.toolCalling);

/** A provider change empties the row's model: the old provider's model means nothing to the new one. */
function ProviderEditCell(props: GridRenderEditCellParams<DraftRow>) {
  const apiRef = useGridApiContext();
  const clearModel = async () => {
    await apiRef.current.setEditCellValue({ id: props.id, field: 'model', value: '' });
  };
  return <GridEditSingleSelectCell {...props} onValueChange={clearModel} />;
}

/**
 * The model of the provider the row is being edited to: a choice from that provider's catalog, or a typed id for a
 * provider whose catalog cannot be read (Anthropic without a key), as the field above the grid does.
 */
function ModelEditCell({ catalogs, ...props }: ModelEditCellProps) {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const editRows = useGridSelector(apiRef, gridEditRowsStateSelector);
  const provider = (editRows[String(props.id)]?.provider?.value as AiProvider | undefined) ?? props.row.provider;
  const offered = offeredBy(catalogs, provider);
  if (offered.length === 0) return <GridEditInputCell {...props} />;

  const colDef = {
    ...props.colDef,
    type: 'singleSelect',
    valueOptions: offered.map((option) => ({ value: option.id, label: aiModelLabel(t, option) })),
  } as typeof props.colDef;
  return <GridEditSingleSelectCell {...props} colDef={colDef} />;
}

interface ColumnContext {
  t: TFunction;
  rows: DraftRow[];
  roles: string[];
  catalogs: Catalogs;
  keyless: (provider: AiProvider) => boolean;
}

function roleModelColumns({ t, rows, roles, catalogs, keyless }: ColumnContext): GridColDef[] {
  // A role a row already names is not offered to another row: only its first row could ever be used.
  const rolesFor = (row: DraftRow | undefined) =>
    roles.filter((role) => role === row?.role || !rows.some((r) => r.role.toLowerCase() === role.toLowerCase()));
  const modelLabel = (row: DraftRow) => {
    const option = offeredBy(catalogs, row.provider).find((m) => m.id === row.model);
    return option ? aiModelLabel(t, option) : row.model;
  };

  return [
    {
      field: 'role',
      headerName: t('aiSettings.role'),
      type: 'singleSelect',
      width: 220,
      editable: true,
      sortable: false,
      valueOptions: ({ row }) => rolesFor(row as DraftRow | undefined),
    },
    {
      field: 'provider',
      headerName: t('aiSettings.provider'),
      type: 'singleSelect',
      width: 160,
      editable: true,
      sortable: false,
      valueOptions: AI_PROVIDERS.map((p) => ({ value: p, label: t(`aiSettings.provider${p}`) })),
      renderEditCell: (params: GridRenderEditCellParams<DraftRow>) => <ProviderEditCell {...params} />,
      renderCell: ({ row }: GridRenderCellParams<DraftRow>) => {
        const label = t(`aiSettings.provider${row.provider}`);
        if (!keyless(row.provider)) return label;
        const warning = t('aiSettings.roleProviderKeyless', { provider: label });
        return (
          <Tooltip title={warning}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {label}
              <WarningIcon fontSize="small" color="warning" />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'model',
      headerName: t('aiSettings.model'),
      // singleSelect for the grid's option handling; the edit cell narrows the options to the row's edited provider.
      type: 'singleSelect',
      valueOptions: ({ row }) => offeredBy(catalogs, (row as DraftRow | undefined)?.provider).map((option) => option.id),
      flex: 1,
      minWidth: 260,
      editable: true,
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams<DraftRow>) => modelLabel(row),
      renderEditCell: (params: GridRenderEditCellParams<DraftRow>) => <ModelEditCell {...params} catalogs={catalogs} />,
    },
  ];
}

interface SectionProps {
  draft: RoleModelsDraft;
  settings: AiAssistantSettings;
}

/**
 * A customer's model per role: the switch and the ordered role → provider + model list, edited like a generated
 * inline child grid (useInlineRowEditing) and ordered by dragging, since the first row naming one of a user's roles
 * is the one used. Off, everyone with the assistant gets the provider and model above. Keys stay per provider: a
 * row only picks which provider's key is used.
 */
export function AiRoleModelsSection({ draft, settings }: SectionProps) {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const catalogs = useAiModelCatalogs();
  const [selection, setSelection] = useState<GridRowSelectionModel>(NO_SELECTION);
  const roles = useMemo(() => settings.roles ?? [], [settings.roles]);
  const providers = settings.providers;

  const { handleAdd, processRowUpdate, typedRowIds, handleGridKeyDown, handleRowEditStop, addButtonRef } = useInlineRowEditing<DraftRow>({
    apiRef,
    data: draft.rows,
    onChange: draft.setRows,
    onPendingEditChange: draft.setTyping,
    newRow: (rows) => newRow({
      role: roles.find((role) => !rows.some((r) => r.role.toLowerCase() === role.toLowerCase())) ?? '',
      provider: settings.provider,
      model: settings.model,
    }),
    focusField: 'role',
  });

  const columns = useMemo(() => roleModelColumns({
    t,
    rows: draft.rows,
    roles,
    catalogs,
    keyless: (provider) => !(providers ?? []).some((p) => p.provider === provider && p.apiKeySet),
  }), [t, draft.rows, roles, catalogs, providers]);

  const deleteSelected = () => {
    draft.setRows(draft.rows.filter((row) => !selection.ids.has(row.id)));
    setSelection(NO_SELECTION);
  };

  return (
    <Paper sx={{ p: 1.5, mb: 1.5 }} elevation={1}>
      <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ mb: 0.5, ...sectionHeadingSx }}>
        {t('aiSettings.sectionRoleModels')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('aiSettings.modelPerRoleHelp')}
      </Typography>
      <FormControlLabel
        control={<Switch size="small" checked={draft.enabled} onChange={(e) => draft.setEnabled(e.target.checked)} />}
        label={t('aiSettings.modelPerRole')}
        sx={{ mb: 1 }}
      />
      {draft.enabled && draft.rows.length === 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>{t('aiSettings.roleModelsEmpty')}</Alert>
      )}
      <Box key={draft.revision} sx={{ display: 'flex', flexDirection: 'column' }} onKeyDown={handleGridKeyDown}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Tooltip title={addRowShortcutLabel()}>
            <Button ref={addButtonRef} variant="outlined" startIcon={<AddIcon />} onClick={handleAdd} size="small" sx={{ textTransform: 'none' }}>
              {t('common.new', { defaultValue: 'New' })}
            </Button>
          </Tooltip>
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={deleteSelected} size="small"
            disabled={selection.ids.size === 0} sx={{ textTransform: 'none' }}>
            {t('common.delete')}
          </Button>
        </Box>
        <DataGridPremium
          apiRef={apiRef}
          density="compact"
          rowHeight={GRID_ROW_HEIGHT}
          rows={draft.rows}
          columns={columns}
          disablePivoting
          disableColumnMenu
          editMode="row"
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          rowReordering
          onRowOrderChange={({ oldIndex, targetIndex }) => draft.setRows(moveRow(draft.rows, oldIndex, targetIndex))}
          hideFooter
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selection}
          onRowSelectionModelChange={(model) => setSelection(toIncludeSelection(model, draft.rows.map((row) => row.id)))}
          localeText={{ noRowsLabel: t('aiSettings.roleModelsNone') }}
          sx={{
            maxHeight: 600,
            ...unsavedRowsSx(draft.unsavedIds),
            ...unsavedRowsSx(typedRowIds),
            ...hoverRevealGridSx,
          }}
        />
      </Box>
    </Paper>
  );
}
